import { Box, Button, Flex, Grid, SimpleGrid, Skeleton, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { openCreatePaymentModel } from "../../../modules/admin-modules/models/Actions";

const C = {
  primary: "#6A1E1E",
  secondary: "#4A0F1F",
  border: "#D8CEC3",
  surface: "#FAF2EB",
  canvas: "#E9E2DB",
  muted: "rgba(74, 15, 31, 0.65)",
};

const PER_PAGE = 10;

const PaymentTable = ({ setSelectedData, page, setPage }) => {
  const students = useSelector((state) => state.students.students);
  const count = useSelector((state) => state.students.count);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.students.isLoading);

  const totalCount = Number(count) || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));

  const list = Array.isArray(students) ? students : [];

  if (isLoading) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height="200px" borderRadius="16px" startColor="gray.200" endColor="gray.300" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!list.length) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          border="1px dashed"
          borderColor={C.border}
          borderRadius="16px"
          bg="rgba(255,255,255,0.65)"
          py={16}
          textAlign="center"
        >
          <Text fontWeight="800" color={C.primary} fontSize="lg">
            No students found
          </Text>
          <Text mt={2} color={C.muted} fontSize="sm" maxW="md" mx="auto">
            Try another search or clear filters to see the full list.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
        {list.map((data, index) => (
          <VStack
            key={data.id}
            align="stretch"
            spacing={4}
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
            p={5}
            bg="rgba(255,255,255,0.92)"
            boxShadow="0 4px 18px rgba(74, 15, 31, 0.06)"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{
              boxShadow: "0 10px 28px rgba(74, 15, 31, 0.1)",
              borderColor: "rgba(106, 30, 30, 0.35)",
            }}
          >
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                  #
                  {(page - 1) * PER_PAGE + index + 1}
                </Text>
                <Text fontSize="lg" fontWeight="800" color={C.secondary} mt={1} noOfLines={2}>
                  {data?.name}
                </Text>
              </Box>
            </Flex>

            <Box pt={1} borderTop="1px solid" borderColor="rgba(216, 206, 195, 0.6)">
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Group
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.secondary} mt={1} noOfLines={1}>
                {data?.group?.name || "—"}
              </Text>
            </Box>

            <VStack spacing={3} align="stretch" pt={1}>
              <Button
                w="100%"
                borderRadius="full"
                fontWeight="800"
                bg={C.primary}
                color="#FAF2EB"
                _hover={{ bg: C.secondary }}
                onClick={() => {
                  setSelectedData(data);
                  dispatch(openCreatePaymentModel());
                }}
              >
                Payment & attendance
              </Button>
            </VStack>
          </VStack>
        ))}
      </Grid>

      {totalPages > 1 ? (
        <Flex mt={8} align="center" justify="center" gap={4} flexWrap="wrap">
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Text fontSize="sm" fontWeight="700" color={C.secondary}>
            Page {page} of {totalPages}
          </Text>
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
};

export default PaymentTable;
